import { useState, useRef, useEffect, useContext } from "react"; 
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import emailjs from "@emailjs/browser";
import { UserContext } from "../context/UserContext"; 
import {
  FaEnvelope, FaLock, FaGoogle, FaUser, FaPhone, FaTimes, FaEye, FaEyeSlash, FaTicketAlt,
} from "react-icons/fa";
import "./StudentSignup.css";
import Loader from "../components/loader";
import PasswordValidator from "./PasswordValidator";

function StudentSignup({ onClose, onBackToLogin, embedded = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh, setUserType } = useContext(UserContext); 

  const [formData, setFormData] = useState({ name: "", email: "", mobile: "", password: "", voucher: "" });
  const [loading, setLoading] = useState(false);
  const [responseMsg, setResponseMsg] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [fullScreenLoading, setFullScreenLoading] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [verificationToken, setVerificationToken] = useState(null);
  const [consent, setConsent] = useState(false);

  const isDarkMode = document.body.classList.contains("dark-mode");
  const formRef = useRef(null);
  const scrollRef = useRef(null);

  const getPostLoginRedirect = () => {
    const storedRedirect =
      sessionStorage.getItem("postLoginRedirect") ||
      localStorage.getItem("postLoginRedirect");
    if (storedRedirect) return storedRedirect;

    const from = location.state?.from;
    if (typeof from === "string") return from;
    if (from?.pathname) {
      return `${from.pathname}${from.search || ""}${from.hash || ""}`;
    }

    return null;
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  useEffect(() => {
    const typedKey = "oauthSignupMessage_student";
    const fallbackKey = "oauthSignupMessage";
    const oauthMessage =
      sessionStorage.getItem(typedKey) || sessionStorage.getItem(fallbackKey);
    if (oauthMessage) {
      setResponseMsg(oauthMessage);
      sessionStorage.removeItem(typedKey);
      sessionStorage.removeItem(fallbackKey);
    }
  }, []);

  useEffect(() => {
    let interval = null;
    if (showVerifyModal && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    } else if (timer === 0) {
      setCanResend(true);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [showVerifyModal, timer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      setResponseMsg("You must agree to the Privacy Policy and Terms & Conditions.");
      return;
    }
    if (!formData.voucher || !/^[A-Za-z0-9]{12}$/.test(formData.voucher)) {
      setResponseMsg("Voucher must be 12 alphanumeric characters");
      return;
    }
    setLoading(true);
    setFullScreenLoading(true);
    try {
      const userId = uuidv4().replace(/-/g, "").slice(0, 16);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/student/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, userId, consent, policyVersion: "v1.0", termsVersion: "v1.0" }),
      });
      const data = await response.json();

      if (response.ok) {
        if (data.verificationToken) {
          setVerificationToken(data.verificationToken);
          localStorage.setItem("verificationRole", "student");
          await sendVerificationEmail(formData.name, formData.email, data.verificationToken);
          setFullScreenLoading(false);
          setShowVerifyModal(true);
        } else {
          setUserType("student");
          if (typeof refresh === 'function') await refresh("student");
          localStorage.setItem("isLoggedIn", "true");
          setResponseMsg("Signup successful!");
          setTimeout(() => {
            setFullScreenLoading(false);
            const pendingAction = localStorage.getItem("postAuthAction");
            // PRIORITIZE ChatBot action (pendingAction) over stale protected-route redirect
            const redirectTo =
              pendingAction || getPostLoginRedirect() || "/dashboard";
            navigate(redirectTo, { replace: true });
            localStorage.removeItem("postAuthAction");
            sessionStorage.removeItem("postLoginRedirect");
            localStorage.removeItem("postLoginRedirect");
          }, 5000);
        }
      } else {
        setResponseMsg(data.message || "Signup failed!");
        setFullScreenLoading(false);
      }
    } catch (error) {
      setResponseMsg("Something went wrong!");
      setFullScreenLoading(false);
    }
    setLoading(false);
  };

  const sendVerificationEmail = async (name, email, token) => {
    const link = `${window.location.origin}/verify-email/${token}`;
    try {
      await emailjs.send("service_5u1kekh", "template_3j87dpi", { to_name: name, user_email: email, verification_link: link }, "5C9M_B-DFS8qIhtRl");
    } catch (error) { console.error("EmailJS Error:", error); }
  };

  const handleResend = () => {
    if (!canResend) return;
    setTimer(30); setCanResend(false);
    sendVerificationEmail(formData.name, formData.email, verificationToken);
  };

  const handleGoogleSignup = () => {
    if (!consent) return;
    setFullScreenLoading(true);
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/student/google/signup`;
  };

  const renderLoaderLayer = () => {
    if (!fullScreenLoading) return null;
    return (
      <div className="fullscreen-loader-wrapper">
        <Loader />
      </div>
    );
  };

  const verifyModal = showVerifyModal ? (
    <div className="verify-modal-overlay-backdrop-x" onClick={onClose}>
      <div className="verify-modal-content-x" onClick={(e) => e.stopPropagation()}>
        <button className="signup-modal-close" onClick={onClose} style={{ color: "white", position: "absolute", top: "16px", right: "18px" }}><FaTimes /></button>
        <img src="/image/email-verified.png" alt="Email Sent" className="verify-icon-img-y" />
        <h3 className="verify-title-z">Email Verification</h3>
        <p className="verify-text-a">Link sent to <span className="verify-email-highlight-b">{formData.email}</span></p>
        <div className="verify-footer-text-e">
          <button onClick={handleResend} disabled={!canResend} className={`verify-resend-btn-f ${canResend ? "enabled" : "disabled"}`}>
            {canResend ? "Resend Email" : `Resend Email (${timer}s)`}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const leftContent = (
    <div className={`signup-left ${fullScreenLoading ? "content-blur" : ""}`} onWheel={(e) => { if (scrollRef.current) scrollRef.current.scrollTop += e.deltaY; }}>
      <div className="signup-card-wrapper" ref={formRef}>
        <div className="signup-header">
          <div className="auth-brand">
            <img src="/image/trans-logo.png" alt="MarsMate" className="auth-brand-icon" />
            <span className="auth-brand-text">MARSMATE AI</span>
          </div>
          <h2 className="signup-title-wrapper">Student Sign Up</h2>
        </div>
        <form onSubmit={handleSubmit} className="signup-form">
          <div className="input-group-signup"><FaUser className="input-icon-signup" /><input name="name" placeholder="Name" onChange={handleChange} required /></div>
          <div className="input-group-signup"><FaEnvelope className="input-icon-signup" /><input name="email" placeholder="Email" onChange={handleChange} required /></div>
          <div className="input-group-signup"><FaPhone className="input-icon-signup" /><input name="mobile" placeholder="Mobile Number" onChange={handleChange} required /></div>
          <div className="input-group-signup">
            <FaLock className="input-icon-signup" /><input type={showPassword ? "text" : "password"} name="password" placeholder="Password" onChange={handleChange} required />
            <span className="toggle-password-icon" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <FaEye /> : <FaEyeSlash />}</span>
          </div>
          <div className="input-group-signup"><FaTicketAlt className="input-icon-signup" /><input name="voucher" placeholder="12-character Voucher" onChange={handleChange} maxLength={12} minLength={12} required pattern="[A-Za-z0-9]{12}" /></div>
          <div className="signup-consent-group">
            <input
              type="checkbox"
              id="signup-consent-student"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              required
            />
            <label htmlFor="signup-consent-student">
              I agree to the <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and <a href="/terms-conditions" target="_blank" rel="noopener noreferrer">Terms & Conditions</a>
            </label>
          </div>
          <PasswordValidator password={formData.password} />
          {responseMsg && <p className="response-msg">{responseMsg}</p>}
          <button className="btn-signup" disabled={loading || !consent}>{loading ? "Processing..." : "Sign Up"}</button>
          <div className="divider-login">or</div>
          <div className="social-signup-wrapper"><button type="button" className="social-btn-google-signup" onClick={handleGoogleSignup} disabled={loading || !consent}><FaGoogle /></button></div>
          <div className="signup-text-login">
            <span>Already have an account? </span>
            <span className="signup-clickable-text" onClick={(e) => { e.stopPropagation(); onBackToLogin(); }}>Login</span>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <>
      {renderLoaderLayer()}
      {embedded ? (
        <>
          {verifyModal}
          {leftContent}
        </>
      ) : (
        <div className="signup-modal-overlay" onClick={onClose}>
          {verifyModal}
          {!showVerifyModal && (
            <div className="modal-box" onClick={(e) => e.stopPropagation()}>
              <div className="signup-modal-split">
                {leftContent}
                <div ref={scrollRef} className="center-scroll-wrapper" onScroll={(e) => { if (formRef.current) formRef.current.style.transform = `translateY(-${e.target.scrollTop}px)`; }}>
                  <div className="center-scroll-content" />
                </div>
                <button className="signup-modal-close" onClick={onClose}><FaTimes /></button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default StudentSignup;
